import { dispatchAction } from '@ardoq/rxbeach';
import {
  deleteModelsRequested,
  DeleteModelsRequestedPayload,
} from './deleteModelsRoutine';
import {
  APIComponentAttributes,
  APIReferenceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import {
  createFilterRequested,
  includeComponentTypeRequested,
  includeReferenceTypeRequested,
} from 'streams/filters/FilterActions';
import { getActiveScenarioState } from 'streams/activeScenario/activeScenario$';
import { IconName } from '@ardoq/icons';
import { TrackedNonComponentGroupContextMenuLabels } from './tracking';
import { type DropdownOption, DropdownOptionType } from '@ardoq/dropdown-menu';
import * as encodingUtils from '@ardoq/html';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { PermissionContext } from '@ardoq/access-control';
import { componentAccessControlOperation } from 'resourcePermissions/accessControlHelpers/component';
import { referenceAccessControlOperation } from 'resourcePermissions/accessControlHelpers/reference';
import { getFeatureContext } from 'streams/featureContext$';
import { activeScenarioOperations, modelOperations } from '@ardoq/core';
import { type WorkspaceAndModelLookup } from '@ardoq/data-model';
import { t, type I18nLocale } from '@ardoq/i18n';

export type TrackingFunction = (optionTitle: string) => void;

export const deleteModels = (deleteArgs: DeleteModelsRequestedPayload) => {
  if (!(deleteArgs.componentIds || deleteArgs.referenceIds)) {
    return;
  }

  dispatchAction(deleteModelsRequested(deleteArgs));
};

export const getPermissionContextUserId = (
  permissionContext: PermissionContext
): ArdoqId | null => permissionContext.user?._id ?? null;

export const includeComponentsOfType = (componentTypeName: string) =>
  dispatchAction(includeComponentTypeRequested(componentTypeName));

export const includeReferencesOfType = (referenceTypeName: string) =>
  dispatchAction(includeReferenceTypeRequested(referenceTypeName));
type CheckHasWriteAccessArgs = {
  workspaceIds: ArdoqId[];
  permissionContext: PermissionContext;
  components?: APIComponentAttributes[];
  references?: APIReferenceAttributes[];
  referenceSourceComponentsById: Record<ArdoqId, APIComponentAttributes>;
};
export const checkHasWriteAccess = ({
  workspaceIds,
  permissionContext,
  components,
  references,
  referenceSourceComponentsById,
}: CheckHasWriteAccessArgs) => {
  const activeScenarioState = getActiveScenarioState();

  const canEditAllWorkSpaces = workspaceIds.every(id =>
    workspaceAccessControlInterface.canEditWorkspace(
      permissionContext,
      id,
      activeScenarioState
    )
  );
  if (
    // If we are in scenario mode, we only care about the scenarios permissions,
    // which is checked on the canEditWorkspace function
    activeScenarioOperations.isInScenarioMode(activeScenarioState) ||
    canEditAllWorkSpaces
  ) {
    return canEditAllWorkSpaces;
  }

  if (!components && !references) {
    return false;
  }

  const featureContext = getFeatureContext();

  const canEditAllComponents =
    !components ||
    components.every(component =>
      componentAccessControlOperation.canEditComponent({
        component,
        permissionContext,
        featureContext,
      })
    );
  const canEditAllReferences =
    !references ||
    references.every(reference =>
      referenceAccessControlOperation.canEditReference({
        reference,
        sourceComponent: referenceSourceComponentsById[reference.source],
        permissionContext,
        featureContext,
      })
    );

  return canEditAllComponents && canEditAllReferences;
};

type GetTypeGroupNameByTypeIdArgs = {
  modelId: string | null | undefined;
  isKnownEntity: boolean;
  workspaceAndModel: WorkspaceAndModelLookup;
};

/**
 * Returns the component-type name when modelId looks up to a component type
 * in a loaded workspace's model (used for the non-component group context
 * menu). Caller must pass isKnownEntity=true when modelId already resolved to
 * a component, reference or workspace — that's a fast path out.
 */
export const getTypeGroupNameByTypeId = ({
  modelId,
  isKnownEntity,
  workspaceAndModel: { workspacesById, modelsById },
}: GetTypeGroupNameByTypeIdArgs): string | undefined => {
  if (!modelId || isKnownEntity) {
    return;
  }

  for (const { componentModel } of Object.values(workspacesById)) {
    const model = modelsById[componentModel];
    if (!model) continue;
    const typeName =
      modelOperations.getComponentTypes(model).componentTypesById[modelId]
        ?.name;
    if (typeName) return typeName;
  }
};

export const getZoomToFitContextMenuItem = (
  onZoomToFitContextMenuItemClick: VoidFunction,
  i18nLocale: I18nLocale,
  trackingFunction?: TrackingFunction
): DropdownOption => ({
  name: 'zoom-to-fit',
  testId: 'zoom-to-fit-item',
  label: t(i18nLocale, 'contextMenus:zoomToFit', 'Zoom to fit'),
  iconName: IconName.ZOOM_TO_FIT,
  onClick: () => {
    onZoomToFitContextMenuItemClick();
    trackingFunction?.(TrackedNonComponentGroupContextMenuLabels.ZOOM_TO_FIT);
  },
  type: DropdownOptionType.OPTION,
});
export const getExpandOrCollapseGroupMenuItem = (
  isGroupOpen: boolean | undefined,
  i18nLocale: I18nLocale,
  onExpandOrCollapseGroupContextMenuItemClick?: VoidFunction,
  trackingFunction?: TrackingFunction
) => ({
  // relying on type safety of expandOrCollapseGroupContextMenuItemProps here
  name: 'toggle-collapse-group',
  testId: 'toggle-collapse-group-item',
  label: isGroupOpen
    ? t(i18nLocale, 'contextMenus:collapseGroup', 'Collapse group')
    : t(i18nLocale, 'contextMenus:expandGroup', 'Expand group'),
  iconName: isGroupOpen ? IconName.COLLAPSE_ALL : IconName.EXPAND_ALL,
  onClick: () => {
    onExpandOrCollapseGroupContextMenuItemClick?.();
    trackingFunction?.(
      isGroupOpen
        ? TrackedNonComponentGroupContextMenuLabels.COLLAPSE_GROUP
        : TrackedNonComponentGroupContextMenuLabels.EXPAND_GROUP
    );
  },
  type: DropdownOptionType.OPTION,
});

export const findElement = (selector: string, target: Element) => {
  const element = target.closest(selector);
  return element instanceof HTMLElement || element instanceof SVGElement
    ? element
    : null;
};

enum WorkspaceReferenceFilterType {
  SOURCE = 'rootWorkspace',
  TARGET = 'targetWorkspace',
}

export const excludeContentFromWorkspace = (
  workspaceId: ArdoqId,
  filterName?: string
) =>
  dispatchAction(
    createFilterRequested({
      name: 'rootWorkspace',
      value: workspaceId,
      affectReference: true,
      affectComponent: true,
      isNegative: true,
      filterName: filterName ? encodingUtils.escapeHTML(filterName) : undefined,
    })
  );
const excludeWorkspaceReferences = (
  workspaceId: ArdoqId,
  referenceFilterType: WorkspaceReferenceFilterType,
  filterName?: string
) =>
  dispatchAction(
    createFilterRequested({
      name: referenceFilterType,
      value: workspaceId,
      affectReference: true,
      affectComponent: false,
      isNegative: true,
      filterName: filterName ? encodingUtils.escapeHTML(filterName) : undefined,
    })
  );
export const excludeReferencesFromWorkspace = (
  workspaceId: ArdoqId,
  filterName?: string
) =>
  excludeWorkspaceReferences(
    workspaceId,
    WorkspaceReferenceFilterType.SOURCE,
    filterName
  );

export const excludeReferencesToWorkspace = (
  workspaceId: ArdoqId,
  filterName?: string
) =>
  excludeWorkspaceReferences(
    workspaceId,
    WorkspaceReferenceFilterType.TARGET,
    filterName
  );

export const excludeChildComponents = (
  componentId: ArdoqId,
  filterName?: string
) =>
  dispatchAction(
    createFilterRequested({
      name: 'parent',
      value: componentId,
      affectComponent: true,
      affectReference: false,
      isNegative: true,
      filterName,
    })
  );

export const excludeComponentsOfType = (componentTypeName: string | null) =>
  dispatchAction(
    createFilterRequested({
      name: 'type',
      value: componentTypeName ?? '',
      affectReference: false,
      affectComponent: true,
      isNegative: true,
    })
  );

export const excludeReferencesOfType = (referenceTypeName: string) =>
  dispatchAction(
    createFilterRequested({
      name: 'type',
      value: referenceTypeName,
      affectReference: true,
      affectComponent: false,
      isNegative: true,
    })
  );

export const createReferencesToTargetFilter = (
  targetComponentId: ArdoqId,
  filterName?: string
) =>
  dispatchAction(
    createFilterRequested({
      name: 'target',
      value: targetComponentId,
      affectReference: true,
      affectComponent: false,
      isNegative: false,
      filterName,
    })
  );

export const createReferencesFromSourceFilter = (
  sourceComponentId: ArdoqId,
  filterName?: string
) =>
  dispatchAction(
    createFilterRequested({
      name: 'source',
      value: sourceComponentId,
      affectReference: true,
      affectComponent: false,
      isNegative: false,
      filterName,
    })
  );

export const getReferenceDisplayName = (
  reference: APIReferenceAttributes,
  fullPathNamesById: Record<ArdoqId, string>
): string => {
  const source = encodingUtils.escapeHTML(
    fullPathNamesById[reference.source] ?? 'Unknown'
  );
  const target = encodingUtils.escapeHTML(
    fullPathNamesById[reference.target] ?? 'Unknown'
  );
  return `${source} -> ${target}`;
};

export const excludeSelected = (
  modelId: ArdoqId,
  isReference: boolean,
  filterName?: string
) =>
  dispatchAction(
    createFilterRequested({
      name: '_id',
      value: modelId,
      affectComponent: !isReference,
      affectReference: isReference,
      isNegative: true,
      filterName,
    })
  );
